import { brazilMarkets, BRAZIL_CURRENCY } from './brazil.js';
import { brazilDriverStoreLinks } from './driver-recruitment.js';
/**
 * The driver simulator's basis (owner, 2026-10-19, Q-353): real Vello driver earnings — R$ 12.194 NET (after the
 * platform's fees) in 400 hours online (1 082 trips). The simulator multiplies the chosen hours by this hour; the page shows the basis and says it is
 * not a guarantee. Replace these figures when a newer period is measured.
 */
export const BRAZIL_DRIVER_EARNINGS_BASIS = { revenueMinor: 1_219_400, onlineHours: 400, trips: 1_082 };
/** The simulator's reference hour (BRL centavos): the real revenue divided by the real hours online. */
export function brazilDriverSimulatorHourlyMinor() {
    return Math.round(BRAZIL_DRIVER_EARNINGS_BASIS.revenueMinor / BRAZIL_DRIVER_EARNINGS_BASIS.onlineHours);
}
/**
 * The coverage map's pins (design v2): each pack market placed by its center inside a frame around the pilot region
 * (the map art is still a placeholder, so the frame is the region, not the whole country).
 */
const MAP_FRAME = { west: -48, east: -45.5, north: -16.5, south: -19.5 };
function cityPlaces() {
    const out = {};
    for (const m of brazilMarkets()) {
        if (m.center === undefined)
            continue;
        const x = ((m.center.longitude - MAP_FRAME.west) / (MAP_FRAME.east - MAP_FRAME.west)) * 100;
        const y = ((MAP_FRAME.north - m.center.latitude) / (MAP_FRAME.north - MAP_FRAME.south)) * 100;
        out[m.name] = { info: 'Projeto-piloto · Minas Gerais', x: Math.round(x), y: Math.round(y) };
    }
    return out;
}
const CONTACT = 'contact';
/** The simulator works in hours online a DAY (owner, 2026-10-19): 1 to 15, a week of 7 days, a month of 30. */
const SIMULATOR_DEFAULT_HOURS = 6;
const SIMULATOR_DAYS = { week: 7, month: 30 };
export const BR_SITE_CONTENT = {
    nav: [
        {
            key: 'home',
            label: 'Passageiro',
            menu: {
                title: 'Para passageiros',
                items: [
                    { icon: 'car', title: 'Corridas de carro', desc: 'Para ir ao trabalho, resolver compromissos ou voltar para casa.', target: 'home#products' },
                    { icon: 'bike', title: 'Vello Moto', desc: 'Uma alternativa prática para circular pela cidade.', target: 'home#products' },
                    { icon: 'qr', title: 'Segurança no embarque', desc: 'Recursos para confirmar sua corrida e viajar com mais tranquilidade.', target: 'safety' },
                    { icon: 'route', title: 'Compartilhe sua viagem', desc: 'Deixe alguém de confiança acompanhar seu trajeto.', target: 'safety' },
                ],
                feature: { kicker: 'Conheça o app', title: 'Seu próximo caminho pode ser com a Vello.', desc: 'Saiba como participar do projeto-piloto.', target: 'download#rider' },
            },
        },
        {
            key: 'drivers',
            label: 'Motorista',
            menu: {
                title: 'Para motoristas',
                items: [
                    { icon: 'car', title: 'Dirigir com a Vello', desc: 'Conheça a plataforma e saiba como começar.', target: 'drivers' },
                    { icon: 'file-check', title: 'Requisitos', desc: 'Confira o que você precisa para se cadastrar.', target: 'drivers#requirements' },
                    { icon: 'wallet', title: 'Ganhos', desc: 'Organize sua rotina e acompanhe seus resultados.', target: 'drivers#earnings' },
                    { icon: 'shield', title: 'Segurança', desc: 'Conheça os recursos disponíveis para motoristas e passageiros.', target: 'safety' },
                ],
                feature: { kicker: 'Simulação de ganhos', title: 'Faça uma simulação de ganhos', desc: 'Veja uma projeção para diferentes jornadas de trabalho.', target: 'drivers#earnings' },
            },
        },
        {
            key: 'business',
            label: 'Empresas',
            menu: {
                title: 'Para empresas',
                items: [
                    { icon: 'briefcase', title: 'Vello Business', desc: 'Soluções de mobilidade para sua empresa.', target: 'business' },
                    { icon: 'store', title: 'Balcão Vello', desc: 'Solicite corridas para seus clientes.', target: 'business#features' },
                    { icon: 'receipt', title: 'Gestão de corridas', desc: 'Acompanhe os pedidos e consulte o faturamento.', target: 'business#features' },
                    { icon: 'headset', title: 'Fale com a equipe', desc: 'Conheça as possibilidades para seu negócio.', target: CONTACT },
                ],
                feature: { kicker: 'Para empresas', title: 'Vamos conversar sobre sua empresa?', desc: 'Falar com a Vello', target: CONTACT },
            },
        },
        { key: 'safety', label: 'Segurança' },
    ],
    navHelp: 'Ajuda',
    navSignIn: 'Entrar',
    // Copy guide §2.3: the apps are not in the stores yet, so the action is to get to know the app, not to download it.
    navDownload: 'Conhecer o app',
    navMenu: 'Menu',
    hero: {
        title: 'Vai de Vello.',
        subtitle: 'Vai sair? Chame um carro ou uma moto e siga seu caminho. Seja para trabalhar, estudar, resolver alguma coisa ou aproveitar o dia.',
        // Leads to the download page, which explains how to join the pilot (copy guide §4.1 operational note).
        ctaPrimary: 'Pedir Vello',
        ctaSecondary: 'Quero dirigir',
        appStore: 'App Store',
        googlePlay: 'Google Play',
        storeNote: 'Apps para iOS e Android em preparação',
        // The live-trip card is a visual demonstration (copy guide §5.2), not a real price or availability.
        cardEta: 'Motorista a 3 min',
        cardPrice: 'R$ 18,90',
        photoNote: 'passageira embarcando, luz natural',
        phases: [
            { title: 'Procurando motorista…', sub: 'Valor consultado · 4,2 km', live: false },
            { title: 'Motorista a 3 min', sub: 'Carlos · Onix prata · BRA2E19', live: true },
            { title: 'Em rota · chegada 14:32', sub: 'Código de embarque conferido', live: true },
        ],
    },
    products: [
        {
            icon: 'car',
            title: 'Corrida',
            body: 'Seu carro para os compromissos de todo dia. Do trabalho à volta para casa, conte com a Vello para chegar onde precisa.',
            target: 'download',
            detail: {
                kicker: 'Corrida',
                title: 'Um carro para seguir com o seu dia.',
                body: 'Tem hora marcada, precisa resolver alguma coisa ou quer voltar para casa? Peça sua corrida pela Vello e vá.',
                bullets: ['Consulte o valor antes de confirmar.', 'Confira os dados do motorista antes de embarcar.', 'Compartilhe seu trajeto com alguém de confiança.'],
                cta: { label: 'Pedir um carro', target: 'download#rider' },
            },
        },
        {
            icon: 'bike',
            title: 'Vello Moto',
            body: 'Quando o trânsito aperta, a moto pode ser uma boa escolha para continuar o dia.',
            target: 'download',
            detail: {
                kicker: 'Vello Moto',
                title: 'De moto, fica mais fácil seguir em frente.',
                body: 'Uma alternativa prática para quem precisa circular pela cidade. Peça sua moto pela Vello e acompanhe a chegada do motociclista.',
                // Owner (2026-10-19): the helmet and following the trip in the app are obvious — never state them.
                bullets: ['Consulte o valor antes de confirmar.', 'Profissionais com documentação verificada.', 'Compartilhe seu trajeto com alguém de confiança.'],
                cta: { label: 'Pedir uma moto', target: 'download#rider' },
            },
        },
        {
            icon: 'briefcase',
            title: 'Vello Business',
            body: 'Hotéis, restaurantes, clínicas e empresas pedem corridas para seus clientes.',
            target: 'business',
            detail: {
                kicker: 'Vello Business',
                title: 'Seu cliente precisa de uma corrida? Sua equipe chama.',
                body: 'Hotéis, restaurantes, clínicas e empresas podem solicitar corridas para seus clientes, mesmo que eles não tenham o aplicativo.',
                bullets: ['Seu cliente não precisa baixar nada.', 'Registros reunidos por período.', 'Um acesso para cada atendente.'],
                cta: { label: 'Conhecer o Vello Business', target: 'business' },
            },
        },
    ],
    stats: [
        { value: 'Para ir e voltar', label: 'Corridas para os compromissos que fazem parte da sua vida.' },
        { value: 'Sua escolha', label: 'Carro ou moto, conforme o que você precisa naquele momento.' },
        { value: 'Mais tranquilidade', label: 'Compartilhe a viagem com quem você quiser.' },
    ],
    mirror: [
        {
            eyebrow: 'PARA VOCÊ',
            title: 'Seu dia tem muitos caminhos.',
            body: 'Trabalho, escola, compras, compromissos ou aquela visita que você estava adiando. Para onde for, a Vello pode acompanhar você.',
            link: 'Conheça o app',
            target: 'download#rider',
            artNote: 'tela inicial do app do passageiro',
        },
        {
            eyebrow: 'PARA DIRIGIR',
            title: 'Seu tempo também tem valor.',
            body: 'Trabalhe nos horários que combinam com sua rotina e escolha as corridas que fazem sentido para você.',
            link: 'Dirigir com a Vello',
            target: 'drivers',
            artNote: 'oferta de corrida no app do motorista',
        },
    ],
    howItWorks: {
        eyebrow: 'COMO FUNCIONA',
        title: 'Seu próximo destino começa aqui.',
        steps: [
            { title: 'Escolha para onde ir', body: 'Informe seu destino e confira as opções disponíveis para a viagem.' },
            { title: 'Encontre seu motorista', body: 'Acompanhe a chegada pelo aplicativo e confira os dados antes de embarcar.' },
            { title: 'Siga seu caminho', body: 'Durante a corrida, acompanhe o trajeto e compartilhe a viagem, se quiser.' },
        ],
    },
    faq: {
        eyebrow: 'PERGUNTAS FREQUENTES',
        title: 'Dúvidas frequentes',
        body: 'Se a sua dúvida não estiver aqui, fale com a gente pela central de ajuda.',
        cta: { label: 'Central de ajuda', target: 'help' },
        items: [
            { question: 'Quanto custa uma corrida?', answer: 'O preço depende do trajeto e da modalidade escolhida. Você consulta o valor antes de confirmar o pedido.' },
            { question: 'Como posso pagar?', answer: 'As formas de pagamento variam conforme as opções disponíveis na sua cidade e no serviço escolhido.' },
            { question: 'Para que serve o código de embarque?', answer: 'Ele ajuda a confirmar que você está embarcando com o motorista da corrida solicitada. Basta informar o código quando ele chegar.' },
            { question: 'Posso pedir uma corrida para outra pessoa?', answer: 'Sim. Você pode informar o endereço de embarque da pessoa e compartilhar os detalhes da viagem com ela.' },
            // `{cities}` is the served-city list (the mechanism stays, copy guide §5.2); the pilot status is stated plainly.
            { question: 'A Vello já está na minha cidade?', answer: 'Hoje a Vello está em projeto-piloto em {cities}. Estamos preparando a Vello para chegar a novos lugares.' },
        ],
    },
    cities: {
        title: 'Onde estamos chegando',
        // The pack's markets; the site build keeps only the ones the API serves now (scripts/gen-site-config.mjs).
        names: brazilMarkets().map((m) => m.name),
        more: '',
        subtitle: 'Conheça as cidades que fazem parte dos nossos primeiros projetos.',
        places: cityPlaces(),
    },
    download: {
        title: 'Seu próximo caminho pode ser com a Vello.',
        body: 'Conheça o aplicativo e descubra uma nova opção para se movimentar pela cidade.',
        appStore: 'App Store',
        googlePlay: 'Google Play',
        cta: { label: 'Conhecer o app', target: 'download' },
    },
    footer: {
        tagline: 'Vello. Para ir, voltar e seguir em frente. Corridas de carro, moto e soluções para empresas.',
        columns: [
            {
                title: 'PRODUTOS',
                links: [
                    { label: 'Corrida', target: 'home#products' },
                    { label: 'Vello Moto', target: 'home#products' },
                    { label: 'Vello Business', target: 'business' },
                ],
            },
            {
                title: 'PARCEIROS',
                links: [
                    { label: 'Dirigir com a Vello', target: 'drivers' },
                    { label: 'Requisitos', target: 'drivers#requirements' },
                    { label: 'Balcão Vello', target: 'business' },
                ],
            },
            {
                title: 'EMPRESA',
                links: [
                    { label: 'Sobre', target: 'about' },
                    { label: 'Segurança', target: 'safety' },
                    { label: 'Carreiras', target: 'careers' },
                ],
            },
            {
                title: 'AJUDA',
                links: [
                    { label: 'Central de ajuda', target: 'help' },
                    { label: 'Termos de uso', target: 'legal:terms' },
                    { label: 'Privacidade', target: 'legal:privacy' },
                ],
            },
        ],
        copyright: '© 2026 Vello Tecnologia Ltda · usevello.app',
        region: 'Brasil · Português',
    },
    // The apps are not in the stores yet: no address, so every badge leads to the download page, which says so. D724: the provider app's addresses are
    // the country pack's "Seja motorista" content (`driver-recruitment.ts`) - the rider app's page and the site read the same ones.
    stores: { rider: {}, driver: brazilDriverStoreLinks() },
    pages: {
        drivers: {
            title: 'Trabalhe no seu ritmo.',
            subtitle: 'Você escolhe quando ficar online e quais corridas quer fazer. Sem precisar deixar sua rotina de lado.',
            ctaPrimary: { label: 'Quero dirigir com a Vello', target: 'download#driver' },
            ctaSecondary: { label: 'Ver requisitos', target: 'drivers#requirements' },
            simulator: {
                eyebrow: 'SIMULAÇÃO DE GANHOS',
                title: 'Faça uma simulação de ganhos',
                support: 'Veja uma projeção para diferentes jornadas de trabalho.',
                hoursLabel: 'Quantas horas pretende dirigir por dia?',
                context: '{hours} h por dia · {weekHours} h online por semana',
                resultLabel: 'Estimativa de ganhos mensais',
                // Owner (2026-10-19): the basis is NET — the platform's fees and the operating costs are already discounted, so the
                // caveat never says costs could lower it.
                note: 'Estimativa com base em ganhos reais de motoristas Vello. O valor já é líquido: as taxas da plataforma e os custos de operação já estão descontados.',
                disclaimer: 'Esta é uma simulação, não uma promessa de ganhos. Os resultados dependem da demanda, das corridas realizadas e de outros fatores.',
                hourlyMinor: brazilDriverSimulatorHourlyMinor(),
                currency: BRAZIL_CURRENCY,
                daysPerWeek: SIMULATOR_DAYS.week,
                resultDays: SIMULATOR_DAYS.month,
                minHours: 1,
                maxHours: 15,
                defaultHours: SIMULATOR_DEFAULT_HOURS,
            },
            features: [
                { icon: 'wallet', title: 'Seus ganhos, sempre à vista', body: 'Acompanhe suas corridas e tenha uma visão clara do que ganhou trabalhando com a Vello.' },
                { icon: 'route', title: 'Você decide quais corridas aceitar', body: 'Escolha as oportunidades que fazem sentido para você. Afinal, quem conhece sua rotina é você.' },
                { icon: 'qr', title: 'Mais tranquilidade no embarque', body: 'O código de confirmação ajuda a identificar o passageiro e evita que a corrida comece com a pessoa errada.' },
            ],
            requirementsTitle: 'O que você precisa para dirigir',
            // The Brazil standard (owner, 2026-10-19); another country pack sets its own list (the KYC catalog is per country).
            requirements: [
                'CNH com EAR (Exerce Atividade Remunerada).',
                'CRLV do veículo em dia.',
                'Seguros exigidos para a atividade.',
                'Certidão de antecedentes criminais.',
                'Comprovante de endereço.',
            ],
            stepsTitle: 'Como começar',
            steps: [
                { title: 'Faça seu cadastro', body: 'Crie seu perfil no aplicativo Vello Prestador.' },
                { title: 'Envie seus documentos', body: 'Acompanhe a análise e o andamento da sua aprovação pelo próprio aplicativo.' },
                { title: 'Comece a dirigir', body: 'Depois da aprovação, fique online para receber oportunidades na sua região.' },
            ],
            artNote: 'motorista parceiro ao lado do carro',
            closing: { title: 'Quer começar a dirigir com a Vello?', body: 'Faça seu cadastro gratuitamente e acompanhe os próximos passos pelo aplicativo.', cta: { label: 'Fazer meu cadastro', target: 'download#driver' } },
        },
        business: {
            eyebrow: 'VELLO BUSINESS',
            title: 'Seu cliente precisa de uma corrida? Você chama.',
            subtitle: 'Com o Balcão Vello, sua equipe pode solicitar corridas para hóspedes, clientes e visitantes, mesmo que eles não tenham o aplicativo.',
            ctaPrimary: { label: 'Falar com a equipe', target: CONTACT },
            ctaSecondary: { label: 'Conhecer o Balcão Vello', target: 'business#features' },
            artNote: 'balcão do parceiro (PDV)',
            features: [
                { icon: 'users', title: 'Seu cliente não precisa baixar nada', body: 'A solicitação é feita pela sua equipe. Mais comodidade para quem precisa de transporte.' },
                { icon: 'receipt', title: 'Tudo organizado em um só lugar', body: 'Consulte as corridas solicitadas e tenha os registros reunidos para o fechamento do período.' },
                { icon: 'sliders', title: 'Um acesso para cada atendente', body: 'Cada pessoa da equipe utiliza sua própria conta, com mais controle sobre as operações.' },
            ],
            band: {
                title: 'Menos trabalho para organizar as corridas.',
                body: 'Da solicitação ao fechamento, sua equipe acompanha os pedidos e encontra os registros em um só lugar. Sem precisar controlar tudo manualmente.',
                facts: [
                    { value: 'Balcão Vello', label: 'Corridas solicitadas pela sua equipe.' },
                    { value: 'Faturamento', label: 'Registros reunidos por período.' },
                ],
            },
        },
        safety: {
            title: 'Mais tranquilidade em cada viagem.',
            subtitle: 'Da confirmação do embarque ao acompanhamento do trajeto, a Vello oferece recursos para você viajar com mais segurança.',
            features: [
                { icon: 'route', title: 'Compartilhe sua viagem', body: 'Envie um link para alguém de confiança acompanhar o trajeto. O acesso termina quando a viagem é encerrada.' },
                { icon: 'qr', title: 'Confirme antes de embarcar', body: 'O código de embarque ajuda a identificar a corrida correta antes de você seguir viagem.' },
                { icon: 'file', title: 'Motoristas com cadastro verificado', body: 'A documentação exigida é analisada durante o cadastro e acompanhada conforme os requisitos da plataforma.' },
                { icon: 'siren', title: 'Precisa de ajuda urgente?', body: 'O aplicativo oferece acesso à ligação para o 190, serviço de emergência policial no Brasil. Para outras situações, entre em contato com a Central Vello.', tone: 'danger' },
            ],
            report: { title: 'Algo aconteceu durante a viagem?', body: 'Conte para a gente o que aconteceu. Nossa equipe vai analisar seu relato e orientar você sobre os próximos passos.', cta: { label: 'Relatar um problema', target: CONTACT } },
        },
        help: {
            title: 'Como podemos ajudar?',
            subtitle: 'Encontre respostas para as principais dúvidas ou converse com a nossa equipe.',
            contact: { title: 'Fale com a Vello', body: 'Precisa resolver alguma coisa? Conte o que aconteceu pelo chat e acompanhe a conversa por aqui.', cta: { label: 'Iniciar conversa', target: CONTACT } },
            // Shown only when the site carries no chat (copy guide §4.5): the real alternative in the app.
            contactFallback: 'Pelo app: abra Conta e toque em Ajuda para falar com a Central.',
            faqTitle: 'Perguntas frequentes',
            faq: [
                { question: 'Como peço uma corrida?', answer: 'Abra o aplicativo Vello, informe o destino e escolha a opção de viagem que preferir. Depois, é só confirmar o pedido.' },
                { question: 'Quais formas de pagamento posso usar?', answer: 'As opções disponíveis dependem da cidade e do serviço. Confira as formas aceitas no momento da solicitação.' },
                { question: 'O que é o código de embarque?', answer: 'É um código usado para confirmar a corrida antes de iniciar a viagem.' },
                { question: 'Em quais cidades a Vello funciona?', answer: 'Você encontra a relação de cidades atendidas na página inicial. A disponibilidade pode variar durante a fase de implantação.' },
                { question: 'Quero ser motorista. Como faço?', answer: 'Acesse a página para motoristas, confira os requisitos e faça seu cadastro pelo aplicativo Vello Prestador.' },
                { question: 'Esqueci alguma coisa no carro. E agora?', answer: 'Entre em contato com a Central e informe os detalhes da corrida. Nossa equipe vai orientar você sobre como tentar recuperar o objeto.' },
                { question: 'Como excluo minha conta?', answer: 'Você pode solicitar a exclusão pelo aplicativo ou pela página de exclusão de conta disponível neste site.' },
            ],
            more: [
                { label: 'Termos de uso', target: 'legal:terms' },
                { label: 'Privacidade', target: 'legal:privacy' },
                { label: 'Segurança', target: 'safety' },
            ],
        },
        about: {
            eyebrow: 'SOBRE',
            title: 'Conheça a Vello.',
            paragraphs: [
                'A Vello nasceu para aproximar quem precisa se deslocar de quem trabalha levando pessoas pela cidade.',
                'Começamos em Minas Gerais, desenvolvendo soluções para passageiros, motoristas e empresas. Nosso objetivo é oferecer uma experiência prática, com informações claras e atenção a quem utiliza a plataforma.',
                'Queremos crescer conhecendo a realidade de cada lugar. Porque cada cidade tem seu jeito de funcionar, e acreditamos que a tecnologia precisa respeitar essas diferenças.',
            ],
            highlights: [
                { icon: 'car', title: 'Para quem precisa ir', body: 'Corridas de carro e moto para os compromissos do dia a dia.' },
                { icon: 'route', title: 'Para quem dirige', body: 'Uma plataforma para encontrar oportunidades e organizar sua rotina de trabalho.' },
                { icon: 'briefcase', title: 'Para quem tem um negócio', body: 'Soluções para empresas que precisam facilitar o deslocamento de clientes, hóspedes e visitantes.' },
            ],
        },
        press: {
            eyebrow: 'IMPRENSA',
            title: 'Imprensa e comunicação',
            paragraphs: [
                'Precisa de informações sobre a Vello, materiais da marca ou quer conversar com nossa equipe?',
                'Entre em contato com a gente e conte o que você precisa.',
            ],
            cta: { title: 'Fale com nossa equipe', body: 'Informe o veículo de comunicação, o assunto e o prazo da sua solicitação.', cta: { label: 'Entrar em contato', target: CONTACT } },
        },
        careers: {
            eyebrow: 'CARREIRAS',
            title: 'Trabalhe com a gente',
            paragraphs: [
                'Estamos construindo a Vello e, neste momento, ainda não temos vagas abertas.',
                'Mas gostamos de conhecer pessoas interessadas em tecnologia e mobilidade. Se quiser se apresentar, entre em contato com a nossa equipe.',
            ],
            cta: { title: 'Quer fazer parte da Vello?', body: 'Conte um pouco sobre você e sua experiência.', cta: { label: 'Enviar mensagem', target: CONTACT } },
            // The openings published in the Admin (Configurações › Vagas do site) replace the paragraphs above while any is open.
            openings: {
                listTitle: 'Vagas abertas',
                intro: 'Estas são as vagas abertas na Vello agora. Escolha a que combina com você e candidate-se.',
                applyLabel: 'Candidatar-se',
                countryWide: 'Brasil',
                readMore: 'Ver descrição da vaga',
            },
        },
        signIn: {
            title: 'Acesse sua área na Vello',
            subtitle: 'Escolha a área que deseja acessar.',
            options: [
                { icon: 'car', title: 'Passageiro', body: 'Peça e acompanhe suas corridas pelo aplicativo Vello.', cta: { label: 'Conhecer o app', target: 'download#rider' } },
                { icon: 'route', title: 'Motorista', body: 'Acesse suas corridas e sua rotina de trabalho pelo Vello Prestador.', cta: { label: 'Vello Prestador', target: 'download#driver' } },
                { icon: 'store', title: 'Balcão Vello', body: 'Solicite e acompanhe corridas para clientes, hóspedes e visitantes.', cta: { label: 'Acessar Balcão Vello', target: 'https://pdv.usevello.app' } },
                { icon: 'package', title: 'Lojista', body: 'Gerencie sua operação pelo portal de parceiros.', cta: { label: 'Acessar portal', target: 'https://parceiro.usevello.app' } },
                { icon: 'users', title: 'Frota', body: 'Acesse as ferramentas de gestão da sua frota.', cta: { label: 'Acessar gestão de frota', target: 'https://frota.usevello.app' } },
            ],
        },
        download: {
            title: 'Os aplicativos da Vello',
            subtitle: 'Escolha o aplicativo para você.',
            apps: [
                { id: 'rider', icon: 'car', eyebrow: 'PASSAGEIRO', title: 'Vello', body: 'Para pedir corridas de carro ou moto e acompanhar suas viagens.' },
                { id: 'driver', icon: 'route', eyebrow: 'MOTORISTA', title: 'Vello Prestador', body: 'Para receber ofertas de corridas, trabalhar com a Vello e acompanhar seus ganhos.' },
            ],
            pending: 'Nossos aplicativos estão sendo preparados para publicação nas lojas. Se você participa do projeto-piloto, entre em contato com a equipe para saber como acessar.',
        },
    },
};
//# sourceMappingURL=site-content.js.map